import React, { MouseEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentSpaceId } from '@core/selectors/space';
import { isSavingSettings, isSpaceMuted } from '@core/notifications/selectors';
import { operations as notificationOperations } from '@app/store/notifications';
import { AulaState } from '@core/types/state';
import {
  Box,
  IconButton,
  Icons,
  Menu,
  MenuItem,
  Radio,
  Theme,
  Tooltip,
  useMediaQuery,
} from '@ublend-npm/aulaui-core';
import { VolumeOffOutlined, VolumeUpOutlined } from '@mui/icons-material';
import { createStyles } from './StudentAction.styles';
import ActionConfirmation from './ActionConfirmation';

type Props = {
  theme: Theme;
};

type SelectedId = 'mute-students' | 'unmute-students';

interface StudentMenuItem {
  label: string;
  id: SelectedId;
}

const StudentAction = ({ theme }: Props) => {
  const styles = createStyles(theme);
  const [menuEl, setMenuEl] = useState<HTMLElement | null>(null);

  const dispatch = useDispatch();
  const spaceId = useSelector(getCurrentSpaceId);
  const saving = useSelector(isSavingSettings);
  const [submitted, setSubmitted] = useState(false);
  const spaceIsMuted = useSelector((state: AulaState) =>
    isSpaceMuted(state, spaceId)
  );

  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const selectedId: SelectedId = spaceIsMuted
    ? 'mute-students'
    : 'unmute-students';
  const [pendingAction, setPendingAction] = useState<{
    id: SelectedId;
    spaceId: string;
    wasMuted: boolean;
  } | null>(null);

  useEffect(() => {
    if (submitted && !saving) {
      setPendingAction(null);
      setSubmitted(false);
    }
  }, [submitted, saving]);

  const displayedIsMuted =
    pendingAction?.spaceId === spaceId ? pendingAction.wasMuted : spaceIsMuted;
  const text = displayedIsMuted ? 'Students muted' : 'Students unmuted';

  const openMenu = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMenuEl(e.currentTarget);
  };

  const closeMenu = () => {
    setMenuEl(null);
  };

  const selectMenu = (menuId: SelectedId) => {
    setPendingAction({ id: menuId, spaceId, wasMuted: spaceIsMuted });
    closeMenu();
  };

  const onClose = () => {
    if (!saving && !submitted) {
      setPendingAction(null);
    }
  };

  const handleConfirm = () => {
    if (saving || submitted) return;

    if (pendingAction?.spaceId === spaceId && pendingAction.id !== selectedId) {
      setSubmitted(true);
      dispatch(notificationOperations.toggleMuteSpace(spaceId));
      return;
    }
    onClose();
  };

  const menuItems: StudentMenuItem[] = [
    { label: 'Mute posts by students', id: 'mute-students' },
    { label: 'Unmute posts by students', id: 'unmute-students' },
  ];

  return (
    <>
      <Box sx={styles.actionWrapper}>
        <Box sx={styles.actionDetails}>
          <Box sx={styles.actionIcon}>
            {displayedIsMuted ? <VolumeOffOutlined /> : <VolumeUpOutlined />}
          </Box>
          <Box sx={styles.actionText}>{text}</Box>
        </Box>
        <Box sx={styles.actionMenuIcon}>
          <IconButton
            id="feed-options"
            type="button"
            aria-label="Student notifications options"
            onClick={openMenu}
            size="small"
            disabled={!spaceId || saving || submitted}
            sx={styles.actionButton}
          >
            {isDesktop ? (
              <Tooltip title="Feed options">
                <Icons.MoreVert />
              </Tooltip>
            ) : (
              <Icons.MoreVert />
            )}
          </IconButton>
        </Box>
      </Box>
      <Menu
        anchorEl={menuEl}
        open={Boolean(menuEl)}
        onClose={closeMenu}
        aria-labelledby="student-notifications-options"
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={styles.menuPaper}
        sx={styles.menuOptions}
      >
        {menuItems.map((item) => (
          <MenuItem
            key={item.id}
            data-testid={`commonroom-${item.id}`}
            label={item.label}
            role="menuitemradio"
            aria-checked={selectedId === item.id}
            disabled={selectedId === item.id}
            onClick={(e) => {
              e.stopPropagation();
              selectMenu(item.id);
            }}
            icon={
              <Radio
                isSelected={selectedId === item.id}
                tabIndex={-1}
                inputProps={{ 'aria-hidden': true }}
                sx={{ pointerEvents: 'none' }}
              />
            }
          />
        ))}
      </Menu>
      <ActionConfirmation
        selectedId={pendingAction?.id ?? selectedId}
        open={Boolean(pendingAction && pendingAction.spaceId === spaceId)}
        onConfirm={handleConfirm}
        saving={saving || submitted}
        onClose={onClose}
        theme={theme}
        isDesktop={isDesktop}
      />
    </>
  );
};

export default StudentAction;
